// Features: M004-F0803
// Tests: T-M004-0443, T-M004-0444, T-M004-0458
// WebUI #267 slice 1 (M004-F0803): the admin room Sensors tab. Read-only. It lists what Mycelium exported for the room:
// describe.sensors[] from GET /v1/rooms/:id/controls (the same read as the Climate tab, via roomControlsApi).
// Fields are those SensorReading defines in room-controls-relay v3 (name, value, unit, observed_at, quality).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import roomControlsApi from '../../services/roomControlsApi';
import roomListsApi from '../../services/roomListsApi';
import {
  applyPages, classifyListFailure, createListState, displayRows,
} from './roomDeviceListModel';
import { isRoomControlsRelayEnabled } from '../../utils/txFeatureFlags';
import './RoomControlsPanel.css';

export const FLAG_OFF_TEXT = 'Room controls are not enabled yet.';
export const EMPTY_TEXT = 'Mycelium has not exported any sensors for this room yet.';
export const NOT_LINKED_TEXT = 'This room is not linked to a Mycelium node yet (Climate tab → Linked Mycelium node).';
export const UNREACHABLE_TEXT = 'The Mycelium node is not reachable right now.';
export const NOT_MAPPED_TEXT = 'Mycelium has no devices mapped for this room yet.';
export const FALLBACK_NOTE = 'Showing climate sensors only';
export const LIST_EMPTY_TEXT = 'Mycelium has not exported any devices for this room yet.';
export const POLL_MS = 30000;
export const GENERIC_TEXT = 'Room sensors could not be loaded right now.';
export const PERMISSION_DENIED_TEXT = 'You do not have permission to view linked devices for this room.';

const QUALITY_WORDS = Object.freeze({ stale: 'Stale', unavailable: 'Unavailable', unknown: 'Unknown', missing: 'Missing' });

// Server error codes are matched by name only; the server message is never rendered.
const codeOf = (cause) => {
  const error = cause?.response?.data?.error;
  if (typeof error === 'string') return error;
  return (error && typeof error.code === 'string' && error.code) || '';
};

export const refusalTextFor = (cause) => {
  const code = codeOf(cause);
  if (code === 'ROOM_NOT_LINKED') return NOT_LINKED_TEXT;
  if (code === 'CSLPT_TARGET_UNAVAILABLE' || code === 'TUNNEL_NODE_TIMEOUT') return UNREACHABLE_TEXT;
  return GENERIC_TEXT;
};

const relativeUpdated = (iso, now = Date.now()) => {
  if (!iso) return 'no update time';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'no update time';
  const minutes = Math.max(0, Math.round((now - then) / 60000));
  if (minutes < 1) return 'updated just now';
  if (minutes < 60) return `updated ${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `updated ${hours} h ago`;
  return `updated ${Math.round(hours / 24)} d ago`;
};

const valueText = (sensor) => (
  sensor.value == null ? 'Not reported' : `${sensor.value}${sensor.unit ? ` ${sensor.unit}` : ''}`
);

const formatCurrent = (n) => (typeof n === 'number' && Number.isFinite(n) ? `${n.toFixed(1)} °C` : 'Not reported');

const LIST_TEXT = Object.freeze({
  not_linked: NOT_LINKED_TEXT,
  unreachable: UNREACHABLE_TEXT,
  not_mapped: NOT_MAPPED_TEXT,
  mismatch: GENERIC_TEXT,
  generic: GENERIC_TEXT,
  permission_denied: PERMISSION_DENIED_TEXT,
});

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

// The describe view of today (Climate-only sensors), used when Core #544 is not live yet (404 NOT_FOUND).
const DescribeView = ({ state }) => {
  const sensors = Array.isArray(state.data?.sensors) ? state.data.sensors : [];
  return (
    <section className="room-controls-panel control-panel room-sensors" data-testid="room-sensors">
      <p className="rc-muted" data-testid="room-sensors-fallback-note">{FALLBACK_NOTE}</p>
      <p className="rc-reading">
        <span>Room temperature</span>{' '}
        <span data-testid="room-sensors-current">{formatCurrent(state.data?.current_temperature)}</span>
      </p>
      {sensors.length === 0 ? (
        <p className="rc-muted" data-testid="room-sensors-empty">{EMPTY_TEXT}</p>
      ) : (
        <ul className="rc-sensors" data-testid="room-sensors-list">
          {sensors.map((sensor, index) => (
            <li key={`${sensor.name}-${index}`} data-testid="room-sensor-row">
              <span>{sensor.name}</span>{' '}
              <span>{valueText(sensor)}</span>{' '}
              <span className="rc-muted">{relativeUpdated(sensor.observed_at)}</span>
              {sensor.quality && sensor.quality !== 'fresh' && (
                <span className="rc-quality"> {QUALITY_WORDS[sensor.quality] || sensor.quality}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const RoomSensorsPanel = ({ roomId }) => {
  const enabled = isRoomControlsRelayEnabled();
  // state: 'loading' | 'list' | 'describe' | 'refused'
  const [state, setState] = useState({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const model = useRef({ list: createListState(), roomUuid: undefined, busy: false, stopped: false });

  const refuse = useCallback((cause, keepList) => {
    const kind = classifyListFailure(cause);
    const text = LIST_TEXT[kind] || GENERIC_TEXT;
    if (kind === 'mismatch') model.current.stopped = true; // one generic error; no retry loop (ROOMLIST-RULE-011)
    if (keepList && model.current.list.watermark !== null && kind !== 'mismatch') {
      setState({ status: 'list', rows: displayRows(model.current.list), problem: text });
    } else {
      setState({ status: 'refused', text, retry: kind === 'mismatch' });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const m = { list: createListState(), roomUuid: undefined, busy: false, stopped: false };
    model.current = m;
    setState({ status: 'loading' });

    const show = () => { if (alive) setState({ status: 'list', rows: displayRows(m.list), problem: null }); };

    const fallbackToDescribe = () => roomControlsApi.describe(roomId)
      .then((data) => { if (alive) setState({ status: 'describe', data }); })
      .catch((cause) => { if (alive) setState({ status: 'refused', text: refusalTextFor(cause) }); });

    const take = async (mode) => {
      const result = mode === 'snapshot'
        ? await roomListsApi.snapshot(roomId, { roomUuid: m.roomUuid })
        : await roomListsApi.changes(roomId, m.list.watermark, m.list.incarnation, { roomUuid: m.roomUuid });
      if (!alive) return 'gone';
      const applied = applyPages(m.list, mode, result.pages);
      if (applied.outcome === 'ok') {
        m.list = applied.state;
        m.roomUuid = result.roomUuid;
      }
      return applied.outcome;
    };

    // Snapshot, or changes; a snapshot_required / incarnation answer leads to one re-snapshot (never a loop).
    const sync = async (initial) => {
      if (m.busy || m.stopped) return;
      m.busy = true;
      try {
        let mode = initial || m.list.watermark === null ? 'snapshot' : 'changes';
        let outcome = await take(mode);
        // One re-snapshot after snapshot_required / an incarnation change; a second refusal is an error, not a loop.
        if (outcome === 'resnapshot') { mode = 'snapshot'; outcome = await take(mode); }
        if (outcome === 'gone') return;
        if (outcome === 'ok') show();
        else if (outcome === 'resnapshot') throw new Error('generic');
        else if (mode === 'snapshot' && m.list.watermark === null) throw new Error('generic');
      } catch (cause) {
        if (!alive) return;
        if (initial && classifyListFailure(cause) === 'fallback') { m.stopped = true; await fallbackToDescribe(); } else refuse(cause, !initial);
      } finally {
        m.busy = false;
      }
    };

    sync(true);
    const tick = () => { if (!isHidden()) sync(false); };
    const timer = setInterval(tick, POLL_MS);
    const onVisible = () => { if (!isHidden()) sync(false); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { alive = false; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [roomId, enabled, attempt, refuse]);

  if (!enabled) {
    return <div className="room-sensors" data-testid="room-sensors"><p className="rc-muted" data-testid="room-sensors-flag-off">{FLAG_OFF_TEXT}</p></div>;
  }

  if (state.status === 'loading') {
    return <div className="room-sensors" data-testid="room-sensors"><p className="rc-muted" role="status" data-testid="room-sensors-loading">Loading sensors…</p></div>;
  }
  if (state.status === 'refused') {
    return (
      <div className="room-sensors" data-testid="room-sensors">
        <p className="rc-reason" role="alert" data-testid="room-sensors-refused">{state.text}</p>
        {state.retry && <button type="button" onClick={() => setAttempt((n) => n + 1)} data-testid="room-sensors-retry">Try again</button>}
      </div>
    );
  }
  if (state.status === 'describe') return <DescribeView state={state} />;

  return (
    <section className="room-controls-panel control-panel room-sensors" data-testid="room-sensors">
      {state.problem && <p className="rc-reason" role="alert" data-testid="room-sensors-problem">{state.problem}</p>}
      {state.rows.length === 0 ? (
        <p className="rc-muted" data-testid="room-sensors-empty">{LIST_EMPTY_TEXT}</p>
      ) : (
        <ul className="rc-sensors" data-testid="room-sensors-list">
          {state.rows.map((row) => (
            <li key={row.key} data-testid="room-device-row">
              <span>{row.label}</span>{' '}
              <span data-testid="room-device-kind">{row.kind.replace(/_/g, ' ')}</span>{' '}
              {' '}<span data-testid="room-device-value">{row.text}</span>{' '}
              <span data-testid="room-device-state">{row.state.replace(/_/g, ' ')}</span>{' '}
              <span className="rc-muted" data-testid="room-device-freshness">{relativeUpdated(row.observed_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default RoomSensorsPanel;
